import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO component for managing dynamic title and meta tags.
 * Falls back to default values if parameters are missing.
 */
export default function SEO({ title, description, image, url, type = "website" }) {
  const defaultTitle = "Temidayo Balogun | Artist, Composer, Producer";
  const defaultDesc = "Official website of Temidayo Balogun — jazz artist, composer, and performer. Explore music, events, gallery, press features, and contact information.";
  const defaultImage = "https://temidayobalogun.com/static/images/temb_music_logo_transparent.png";
  const defaultUrl = "https://temidayobalogun.com/";

  // Resolve base site origin (stripping /api/v1 if derived from REACT_APP_API_URL)
  let baseOrigin = "https://temidayobalogun.com";

  if (process.env.REACT_APP_SITE_URL) {
    baseOrigin = process.env.REACT_APP_SITE_URL.replace(/\/+$/, "");
  } else if (process.env.REACT_APP_API_URL) {
    const rawApi = process.env.REACT_APP_API_URL;
    const apiBase = rawApi.split("/api")[0];
    if (apiBase && !apiBase.includes("localhost") && !apiBase.includes("127.0.0.1")) {
      baseOrigin = apiBase.replace(/\/+$/, "");
    }
  } else if (
    typeof window !== "undefined" &&
    window.location.origin &&
    !window.location.origin.includes("localhost") &&
    !window.location.origin.includes("127.0.0.1")
  ) {
    baseOrigin = window.location.origin;
  }

  // Format title
  let seoTitle = defaultTitle;
  if (title && title.trim()) {
    const cleanTitle = title.trim();
    seoTitle = cleanTitle.includes("Temidayo Balogun") ? cleanTitle : `${cleanTitle} | Temidayo Balogun`;
  }

  const seoDesc = description ? description.trim() : defaultDesc;

  // Resolve canonical URL
  let seoUrl = defaultUrl;
  if (url) {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      seoUrl = url;
    } else {
      seoUrl = `${baseOrigin}${url.startsWith("/") ? "" : "/"}${url}`;
    }
  }

  // Resolve and clean image path
  let seoImage = defaultImage;
  if (image) {
    let cleanImage = image;
    // Clean local development absolute URLs containing loopback domains
    if (image.includes("localhost") || image.includes("127.0.0.1")) {
      if (image.includes("/static/")) {
        cleanImage = "/static/" + image.split("/static/")[1];
      } else if (image.includes("/media/")) {
        cleanImage = "/media/" + image.split("/media/")[1];
      } else if (image.includes("/uploads/")) {
        cleanImage = "/uploads/" + image.split("/uploads/")[1];
      }
    }

    if (cleanImage.startsWith("http://") || cleanImage.startsWith("https://")) {
      seoImage = cleanImage;
    } else {
      seoImage = `${baseOrigin}${cleanImage.startsWith("/") ? "" : "/"}${cleanImage}`;
    }
  }

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph / Facebook Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Temidayo Balogun Official Website" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDesc} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={seoUrl} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDesc} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:url" content={seoUrl} />
    </Helmet>
  );
}
